import { Layout } from "@/components/layout/Layout";
import { Seo } from "@/components/Seo";
import { ExternalLink, Search, Loader2, ArrowUpDown, Play } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/community/client";
import { ImpactCircleSection } from "@/components/ImpactCircleSection";

interface StoryItem {
  id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  category: string;
  published_at: string | null;
  medium_link: string | null;
}

interface VideoItem {
  id: string;
  title: string;
  youtube_id: string;
  description: string | null;
  is_featured: boolean | null;
  display_order: number | null;
}

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?w=1200";

const formatDate = (dateString: string | null) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const youtubeUrl = (youtubeId: string) =>
  `https://www.youtube.com/watch?v=${youtubeId}`;

const Kicker = ({ children }: { children: React.ReactNode }) => (
  <span className="font-display font-bold uppercase text-[0.7rem] tracking-[0.18em] text-primary">
    {children}
  </span>
);

const SectionHeading = ({ label }: { label: string }) => (
  <div className="flex items-center gap-4 mb-8">
    <div className="h-3 w-3 bg-secondary" aria-hidden="true" />
    <h2 className="font-display font-extrabold uppercase text-2xl md:text-3xl tracking-tight text-foreground">
      {label}
    </h2>
    <div className="flex-1 h-px bg-black/15" aria-hidden="true" />
  </div>
);

const StoryCard = ({ post }: { post: StoryItem }) => (
  <a
    href={post.medium_link || "https://medium.com/@ecocupid.asean"}
    target="_blank"
    rel="noopener noreferrer"
    className="group block"
  >
    <article className="h-full">
      <div className="relative aspect-[16/10] overflow-hidden mb-4">
        <img
          src={post.thumbnail_url || FALLBACK_IMG}
          alt={post.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <Kicker>{post.category}</Kicker>
      <h3 className="font-display font-bold text-xl text-foreground mt-2 mb-2 line-clamp-3 leading-tight group-hover:text-primary transition-colors">
        {post.title}
      </h3>
      {post.description && (
        <p className="font-sans text-black/70 text-sm line-clamp-2 leading-relaxed mb-2">
          {post.description}
        </p>
      )}
      <p className="font-sans text-xs uppercase tracking-wide text-black/50">
        {formatDate(post.published_at)} · Read on Medium
      </p>
    </article>
  </a>
);

const Stories = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const [stories, setStories] = useState<StoryItem[]>([]);
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [categories, setCategories] = useState<string[]>(["All"]);
  const [isLoading, setIsLoading] = useState(true);
  

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);

      const [{ data: blogData }, { data: videoData }] = await Promise.all([
        supabase
          .from("blog_articles")
          .select("*")
          .eq("is_hidden", false)
          .order("display_order", { ascending: true })
          .order("published_at", { ascending: false }),
        supabase
          .from("youtube_videos")
          .select("*")
          .order("is_featured", { ascending: false })
          .order("display_order", { ascending: true }),
      ]);

      const all: StoryItem[] = (blogData || []).map((d) => ({
        id: d.id,
        title: d.title,
        description: d.description,
        thumbnail_url: d.thumbnail_url,
        category: d.category,
        published_at: d.published_at,
        medium_link: d.medium_link,
      }));

      setStories(all);
      setVideos((videoData as VideoItem[]) || []);
      const uniqueCategories = ["All", ...new Set(all.map((a) => a.category).filter(Boolean))];
      setCategories(uniqueCategories);
      setIsLoading(false);
    };

    fetchAll();
  }, []);

  const isFiltering = searchQuery.trim() !== "" || selectedCategory !== "All";

  const filteredPosts = stories
    .filter((post) => {
      const matchesSearch =
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (post.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
      const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      const dateA = a.published_at ? new Date(a.published_at).getTime() : 0;
      const dateB = b.published_at ? new Date(b.published_at).getTime() : 0;
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

  const lead = filteredPosts[0];
  const secondary = filteredPosts.slice(1, 5);
  const rest = filteredPosts.slice(5);

  const featuredVideo = videos[0];
  const moreVideos = videos.slice(1, 7);

  return (
    <Layout>
      <Seo
        title="EcoCupid - Stories & Films from a Greener Southeast Asia"
        description="Articles, photo essays, documentaries, and short films on conservation, climate, and grassroots eco-projects across Southeast Asia."
        path="/"
      />

      {isLoading ? (
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* ── Featured lead story ── */}
          {lead && (
            <section className="bg-foreground" aria-labelledby="lead-heading">
              <a
                href={lead.medium_link || "https://medium.com/@ecocupid.asean"}
                target="_blank"
                rel="noopener noreferrer"
                className="group block relative"
              >
                <div className="relative min-h-[60vh] md:min-h-[78vh] flex items-end overflow-hidden">
                  <img
                    src={lead.thumbnail_url || FALLBACK_IMG}
                    alt={lead.title}
                    fetchPriority="high"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  />
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent"
                    aria-hidden="true"
                  />
                  <div className="container mx-auto px-4 relative pb-12 md:pb-20 pt-28">
                    <div className="max-w-3xl text-white">
                      <span className="font-display font-bold uppercase text-xs md:text-sm tracking-[0.2em] text-secondary">
                        {lead.category}
                      </span>
                      <h1
                        id="lead-heading"
                        className="font-display font-extrabold text-4xl md:text-6xl leading-[1.03] tracking-tight mt-4 mb-4"
                      >
                        {lead.title}
                      </h1>
                      {lead.description && (
                        <p className="font-sans text-lg md:text-xl text-white/90 leading-relaxed max-w-2xl mb-5 line-clamp-3">
                          {lead.description}
                        </p>
                      )}
                      <span className="inline-flex items-center gap-2 font-display font-bold uppercase text-sm tracking-[0.12em] border-b-2 border-secondary pb-1 group-hover:gap-3 transition-all">
                        Read the story <ExternalLink className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </a>
            </section>
          )}

          {/* ── Filter / search bar ── */}
          <section className="border-b border-black/10 bg-background">
            <div className="container mx-auto px-4 py-5">
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                <div className="flex items-center gap-3 w-full lg:w-auto">
                  <div className="relative flex-1 lg:w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/50" />
                    <Input
                      placeholder="Search articles..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-10 rounded-none border-black/20 bg-transparent"
                    />
                  </div>
                  <Select value={sortOrder} onValueChange={(value: "newest" | "oldest") => setSortOrder(value)}>
                    <SelectTrigger className="w-[140px] h-10 rounded-none border-black/20">
                      <ArrowUpDown className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Sort by date" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Newest first</SelectItem>
                      <SelectItem value="oldest">Oldest first</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`font-display font-bold uppercase text-xs tracking-[0.12em] pb-1 border-b-2 transition-colors ${
                        selectedCategory === category
                          ? "border-secondary text-primary"
                          : "border-transparent text-black/60 hover:text-primary"
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ── Latest stories ── */}
          <section className="py-14 md:py-20 bg-background" aria-labelledby="latest-heading">
            <div className="container mx-auto px-4">
              <h2 id="latest-heading" className="sr-only">Latest stories</h2>
              <SectionHeading label="Latest Stories" />

              {filteredPosts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="font-sans text-black/70">No articles found matching your criteria.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                  {/* Wide feature */}
                  {secondary[0] && (
                    <div className="lg:col-span-7">
                      <a
                        href={secondary[0].medium_link || "https://medium.com/@ecocupid.asean"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group block"
                      >
                        <div className="relative aspect-[16/9] overflow-hidden mb-5">
                          <img
                            src={secondary[0].thumbnail_url || FALLBACK_IMG}
                            alt={secondary[0].title}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <Kicker>{secondary[0].category}</Kicker>
                        <h3 className="font-display font-extrabold text-2xl md:text-3xl text-foreground mt-2 mb-3 leading-tight group-hover:text-primary transition-colors">
                          {secondary[0].title}
                        </h3>
                        {secondary[0].description && (
                          <p className="font-sans text-black/70 leading-relaxed line-clamp-3 mb-2">
                            {secondary[0].description}
                          </p>
                        )}
                        <p className="font-sans text-xs uppercase tracking-wide text-black/50">
                          {formatDate(secondary[0].published_at)} · Read on Medium
                        </p>
                      </a>
                    </div>
                  )}

                  {/* Stacked side list */}
                  <div className="lg:col-span-5 flex flex-col divide-y divide-black/10">
                    {secondary.slice(1).map((post) => (
                      <a
                        key={post.id}
                        href={post.medium_link || "https://medium.com/@ecocupid.asean"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex gap-4 py-4 first:pt-0"
                      >
                        <div className="relative w-28 h-20 flex-shrink-0 overflow-hidden">
                          <img
                            src={post.thumbnail_url || FALLBACK_IMG}
                            alt={post.title}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="min-w-0">
                          <Kicker>{post.category}</Kicker>
                          <h4 className="font-display font-bold text-base text-foreground mt-1 line-clamp-3 leading-tight group-hover:text-primary transition-colors">
                            {post.title}
                          </h4>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ── Watch (videos) ── */}
          {videos.length > 0 && !isFiltering && (
            <section className="py-14 md:py-20 bg-card border-y border-black/10" aria-labelledby="watch-heading">
              <div className="container mx-auto px-4">
                <h2 id="watch-heading" className="sr-only">Watch</h2>
                <SectionHeading label="Watch" />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                  {/* Featured video */}
                  {featuredVideo && (
                    <div className="lg:col-span-7">
                      <a
                        href={youtubeUrl(featuredVideo.youtube_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group block relative aspect-video overflow-hidden bg-black mb-4"
                        aria-label={`Watch ${featuredVideo.title} on YouTube`}
                      >
                        <img
                          src={`https://img.youtube.com/vi/${featuredVideo.youtube_id}/maxresdefault.jpg`}
                          alt={featuredVideo.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                          <span className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center">
                            <Play className="w-7 h-7 text-black fill-black ml-1" />
                          </span>
                        </span>
                      </a>
                      <a
                        href={youtubeUrl(featuredVideo.youtube_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-block"
                      >
                        <h3 className="font-display font-extrabold text-2xl text-foreground leading-tight mb-2 group-hover:text-primary transition-colors">
                          {featuredVideo.title}
                        </h3>
                      </a>
                      {featuredVideo.description && (
                        <p className="font-sans text-black/70 leading-relaxed line-clamp-2">
                          {featuredVideo.description}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Video list */}
                  <div className="lg:col-span-5 flex flex-col divide-y divide-black/10">
                    {moreVideos.map((video) => (
                      <a
                        key={video.id}
                        href={youtubeUrl(video.youtube_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex gap-4 py-4 first:pt-0 text-left"
                        aria-label={`Watch ${video.title} on YouTube`}
                      >
                        <div className="relative w-32 h-20 flex-shrink-0 overflow-hidden bg-black">
                          <img
                            src={`https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`}
                            alt={video.title}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/15 group-hover:bg-black/30 transition-colors">
                            <span className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center">
                              <Play className="w-4 h-4 text-black fill-black ml-0.5" />
                            </span>
                          </span>
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-display font-bold text-base text-foreground line-clamp-3 leading-tight group-hover:text-primary transition-colors">
                            {video.title}
                          </h4>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* ── More stories ── */}
          {rest.length > 0 && (
            <section className="py-14 md:py-20 bg-background" aria-labelledby="more-heading">
              <div className="container mx-auto px-4">
                <h2 id="more-heading" className="sr-only">More stories</h2>
                <SectionHeading label="More Stories" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                  {rest.map((post) => (
                    <StoryCard key={post.id} post={post} />
                  ))}
                </div>
              </div>
            </section>
          )}

          <ImpactCircleSection variant="standalone" />
        </>
      )}
    </Layout>
  );
};

export default Stories;
